"use client";
import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Avatar, Skeleton, Modal } from "antd";
import { UserOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import codematicLogo from "../../assets/codematic-logo.png";
import { ChevronDown } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
  


const Navbar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isPerformanceDropdownOpen, setIsPerformanceDropdownOpen] = useState(false);
  const [isSubmissionsMenuExpanded, setIsSubmissionsMenuExpanded] = useState(false);
  const performanceDropdownRef = useRef<HTMLDivElement>(null);
  const submissionsDropdownRef = useRef<HTMLDivElement>(null);

  // Use the custom auth hook
  const { 
    isAuthenticated, 
    isLoadingProfile, 
    profileError,
    logout,
    getUserDisplayName,
    getUserEmail,
    getUserInitials
  } = useAuth();

  const handleLogout = () => {
    Modal.confirm({
      title: 'Confirm Logout',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to logout?',
      okText: 'Logout',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        logout();
        // Use replace to prevent back button access
        navigate('/login', { replace: true });
      },
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check submissions dropdown
      if (submissionsDropdownRef.current && !submissionsDropdownRef.current.contains(event.target as Node)) {
        setIsSubmissionsMenuExpanded(false);
      }
      // Check performance dropdown
      if (performanceDropdownRef.current && !performanceDropdownRef.current.contains(event.target as Node)) {
        setIsPerformanceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const isActive = (href: string) => pathname === href;

  return (
    <header className="border-b bg-white px-6 py-4 onest shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-8">
          <Link to="/dashboard">
            <img src={codematicLogo} alt="Codematic Logo" className="h-8 w-auto" />
          </Link>
        </div>
        <nav className="md:flex space-x-6 hidden">
          <Link
            to="/dashboard"
            className={`text-base font-medium ${
              isActive("/dashboard")
                ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                : "text-gray-600 hover:text-gray-900 px-2 py-1"
            }`}
          >
            Home
          </Link>
          <Link
            to="/hackathons"
            className={`text-base font-medium ${
              isActive("/hackathons")
                ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                : "text-gray-600 hover:text-gray-900 px-2 py-1"
            }`}
          >
            Hackathons
          </Link>
          <Link
            to="/participants"
            className={`text-base font-medium ${
              isActive("/participants")
                ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                : "text-gray-600 hover:text-gray-900 px-2 py-1"
            }`}
          >
            Participants
          </Link>
       
         
             <Link
               to="/rubric"
               className={`text-base font-medium ${
                 isActive("/rubric")
                   ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                   : "text-gray-600 hover:text-gray-900 px-2 py-1"
               }`}
             >
               Rubric
             </Link>

             {/* <Link
               to="/submissions"
               className={`text-base font-medium ${
                 isActive("/submissions")
                   ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                   : "text-gray-600 hover:text-gray-900 px-2 py-1"
               }`}
             >
               Submissions
             </Link> */}
        

          <div className="relative" ref={submissionsDropdownRef}>
            <button
              className={`text-base font-medium flex items-center gap-1 ${
                isActive("/submissions") || isActive("/weekly-submissions")
                  ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                  : "text-gray-600 hover:text-gray-900 px-2 py-1"
              }`}
              type="button"
              onClick={() => setIsSubmissionsMenuExpanded(!isSubmissionsMenuExpanded)}
            >
              Submissions
              <ChevronDown className={`h-4 w-4 transition-transform ${isSubmissionsMenuExpanded ? 'rotate-180' : ''}`} />
            </button>
            {isSubmissionsMenuExpanded && (
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-auto min-w-[250px] max-w-[250px] bg-white border border-gray-200 rounded-md shadow-lg z-20">
                <div className="p-4">
                  <div className="flex flex-col justify-center w-full">
                    <Link
                      to="/weekly-submissions"
                      className="block p-2 hover:bg-gray-50 rounded-md transition-colors w-full"
                      onClick={() => setIsSubmissionsMenuExpanded(false)}
                    >
                      <div className="flex gap-3 items-start">
                      
                        <div className="flex flex-col gap-1">
                          <h1 className="text-base  text-gray-900">Weekly Submissions</h1>

                        </div>
                      </div>
                    </Link>
                    <Link
                      to="/submissions"
                      className="block p-2 hover:bg-gray-50 rounded-md transition-colors w-full"
                      onClick={() => setIsSubmissionsMenuExpanded(false)}
                    >
                      <div className="flex gap-3 items-start">
                       
                        <div className="flex flex-col gap-1">
                          <h1 className="text-base  text-gray-900">FInal Submissions</h1>
                          <p className="text-sm text-gray-600"></p>
                        </div>
                      </div>
                    </Link>

                    <Link
                      to="/projects"
                      className="block p-2 hover:bg-gray-50 rounded-md transition-colors w-full"
                      onClick={() => setIsSubmissionsMenuExpanded(false)}
                    >
                      <div className="flex gap-3 items-start">
                       
                        <div className="flex flex-col gap-1">
                          <h1 className="text-base  text-gray-900">Projects</h1>
                          <p className="text-sm text-gray-600"></p>
                        </div>
                      </div>
                    </Link>
                  
                   
                  
                  </div>
                </div>
              </div>
            )}
          </div>


          <div className="relative" ref={performanceDropdownRef}>
            <button
              className={`text-base font-medium flex items-center gap-1 ${
                isActive("/performance")
                  ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                  : "text-gray-600 hover:text-gray-900 px-2 py-1"
              }`}
              type="button"
              onClick={() => setIsPerformanceDropdownOpen(!isPerformanceDropdownOpen)}
            >
              Resources
              <ChevronDown className={`h-4 w-4 transition-transform ${isPerformanceDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isPerformanceDropdownOpen && (
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-auto min-w-[200px] max-w-[200px] bg-white border border-gray-200 rounded-md shadow-lg z-20">
                <div className="p-4">
                  <div className="flex flex-col justify-center w-full">
                    <Link
                      to="/events"
                      className="block p-2 hover:bg-gray-50 rounded-md transition-colors w-full"
                      onClick={() => setIsSubmissionsMenuExpanded(false)}
                    >
                      <div className="flex gap-3 items-start">
                      
                        <div className="flex flex-col gap-1">
                          <h1 className="text-base  text-gray-900">Events</h1>

                        </div>
                      </div>
                    </Link>
                    <Link
                      to="/resources"
                      className="block p-2 hover:bg-gray-50 rounded-md transition-colors w-full"
                      onClick={() => setIsSubmissionsMenuExpanded(false)}
                    >
                      <div className="flex gap-3 items-start">
                       
                        <div className="flex flex-col gap-1">
                          <h1 className="text-base  text-gray-900">Resources</h1>
                          <p className="text-sm text-gray-600"></p>
                        </div>
                      </div>
                    </Link>
                  
                      <Link
                        to="/countdown"
                        className="block p-2 hover:bg-gray-50 rounded-md transition-colors w-full"
                        onClick={() => setIsSubmissionsMenuExpanded(false)}
                      >
                        <div className="flex gap-3 items-start">
                        
                          <div className="flex flex-col gap-1">
                            <h1 className="text-base  text-gray-900">Countdown</h1>
                           
                          </div>
                        </div>
                      </Link>
                  
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* {isAdmin && (
            <Link
              href="/logs"
              className={`text-base font-medium ${
                isActive("/logs")
                  ? "text-gray-900 bg-gray-50 px-2 py-1 rounded-md"
                  : "text-gray-600 hover:text-gray-900 px-2 py-1"
              }`}
            >
              Audit logs
            </Link>
          )} */}

        </nav>

        <Dropdown
          menu={{
            items: [
             
              {
                key: 'logout',
                label: 'Log out',
                onClick: handleLogout
              }
            ]
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded-md transition-colors">
            {isLoadingProfile ? (
              <>
                <Skeleton.Avatar size={32} active />
                <div className="text-sm hidden md:block">
                  <Skeleton.Input size="small" active style={{ width: 80, marginBottom: 4 }} />
                  <Skeleton.Input size="small" active style={{ width: 120 }} />
                </div>
              </>
            ) : (
              <>
                <Avatar 
                  size={32}
                  icon={<UserOutlined />}
                >
                  {getUserInitials()}
                </Avatar>
                <div className="text-sm hidden md:block">
                  <div className="font-medium text-gray-900">
                    {getUserDisplayName()}
                  </div>
                  <div className="text-gray-500">
                    {getUserEmail()}
                  </div>
                </div>
              </>
            )}
            <ChevronDown size={14} />
          </div>
        </Dropdown>
      </div>
    </header>
  );
};

export default Navbar;
